import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Reload } from "src/components";
import { ProgramType } from "src/enums";
import { UIService, _ } from "src/services";
import {
  ProgramsConductor,
  ProgramsConductorAction,
  UserSession,
} from "src/store";
import { DayCard } from "./day.card";
import { ProgramConductorProps } from "./product.conductor.props";
import { ProgramCardShimmer } from "./program.cad.shimmer";
import { ProgramConductorShimmer } from "./program.conductor.shimmer";
import Controls from "src/components/Controls";
import { Container, ListGroup } from "react-bootstrap";
import { Conductor, Program, Promotions } from "src/models";
import ListsCard from "src/components/ProgramList/ListsCard";

export const ProgramConductor: React.FC<ProgramConductorProps> = ({
  showFeatured = true,
}) => {
  //#region Dependencies

  const dispatch = useDispatch();
  const conductor: ProgramsConductor = useSelector(
    (state: any) => state.ProgramsConductorState
  );
  const user: UserSession = useSelector((state: any) => state.UserState);

  const [liveShows, setLiveShows] = useState<
    {
      program?: Program | undefined;
      promotion?: Promotions | undefined;
    }[]
  >([]);

  useEffect(() => {
    const lives = conductor.list?.[0]?.items.filter(
      (item) => item.program?.status === 2
    );

    setLiveShows(lives);
  }, [conductor.list]);

  //#endregion

  //#region properties

  const moreRef = useRef<HTMLDivElement>(null);

  const programCount = conductor.list.reduce(
    (x, y) => x + y.items.filter((a) => a.program).length,
    0
  );

  //#endregion

  //#region mehtods

  const fetchData = useCallback(
    (clear: boolean, clearBeforeFetch?: boolean) => {
      if (conductor.loading) return;

      const offset = clear ? 0 : conductor.offset;

      dispatch(
        ProgramsConductorAction.getData(
          conductor.count,
          ProgramType.Live,
          offset,
          showFeatured,
          clear,
          clearBeforeFetch
        )
      );
    },
    [dispatch, conductor, showFeatured]
  );

  const loadMore = useCallback(() => fetchData(false), [fetchData]);

  const reload = useCallback(
    (clearBeforeFetch?: boolean) => fetchData(true, clearBeforeFetch),
    [fetchData]
  );

  const handleScroll = useCallback(
    (e: Event) => {
      if (
        !conductor.list.length ||
        !conductor.hasMore ||
        conductor.offset > 4 * conductor.count ||
        conductor.loading
      )
        return;

      const isInView = UIService.isInView(moreRef);

      if (isInView) loadMore();
    },
    [conductor, loadMore]
  );

  //#endregion

  //#region effects

  useEffect(() => {
    if (
      conductor.list.length &&
      conductor.list.every((x) =>
        x.items.every((y) => !y.program || !y.program.origin)
      )
    )
      return;

    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loggedIn]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    document.addEventListener("touchmove", handleScroll, false);

    return () => {
      document.removeEventListener("scroll", handleScroll);
      document.removeEventListener("touchmove", handleScroll, false);
    };
  }, [handleScroll]);

  //#endregion

  //#region  helpers

  const renderLoadMore = () => {
    if (
      !conductor.hasMore ||
      conductor.totalRecords <= programCount ||
      conductor.offset >= conductor.totalRecords
    )
      return null;

    return (
      <div className="db-blcok h-4" ref={moreRef}>
        {conductor.list.length > 0 && !conductor.loading && (
          <button
            onClick={loadMore}
            className="db-block btn btn-link load-more"
          >
            {_("more")}
          </button>
        )}
        {conductor.list.length > 0 &&
          conductor.loading &&
          [...new Array(2)].map((x, index) => (
            <ProgramCardShimmer key={index} />
          ))}
      </div>
    );
  };

  const renderTitle = () => {
    return (
      <div className="archive-list-area">
        <div className="clearfix">
          <h5 className="float-right text-rtl text-right section-title">
            {_("programList")}
          </h5>
          <button
            title={_("reload")}
            type="button"
            onClick={() => reload(false)}
            className={`float-left btn btn-sm button-dark btn-refresh btn-secondary text-rtl${
              conductor.loading ? " btn-loading" : ""
            }`}
          >
            <span className="icon">
              <Reload />
            </span>
            <span className="text">{_("reload")}</span>
          </button>
        </div>
      </div>
    );
  };

  //#endregion

  const showShimmer =
    conductor.firstLoad || (!conductor.list.length && conductor.loading);
  const showEmpty =
    !conductor.firstLoad && !conductor.list.length && !conductor.loading;

  useEffect(() => {
    reload(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container fluid style={{ marginTop: "20px" }}>
      {liveShows?.length && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              textAlign: "right",
              margin: "5px 0",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                background: "#B71C1C",
                borderRadius: "14px",
                flex: "none",
                order: "0",
                flexGrow: "0",
                margin: " 2px 6px",
              }}
            />
            <span
              style={{
                lineHeight: 1.5,
                color: "#fff",
              }}
            >
              پخش زنده
            </span>
          </div>
          <ListGroup
            horizontal
            style={{ overflow: "auto", textAlign: "right" }}
            className="selectableRow"
          >
            {liveShows.map((item, ind: number) => (
              <ListsCard
                isLive={true}
                cover={item.program?.cover}
                programId={item.program?.id}
                startAt={"زنده"}
                title={item.program?.title!}
                upperTitle={item.program?.upperTitle}
                status={item.program?.status}
                liveBadgeText={item.program?.liveBadgeText}
                id={item.program?.id}
                key={ind}
              />
            ))}
          </ListGroup>
        </>
      )}
      {conductor.list
        .filter((day) => day.items.length)
        .map((day, index) => (
          <DayCard key={index} {...day} mainIndex={index} />
        ))}
      {renderLoadMore()}

      {conductor.list.length > 0 && <Controls />}
    </Container>
  );
};
